import { LabourCostMethod } from '../enums/labour-cost-method.enum';
import { getMoneySchema, Money } from '../utils/money.model';

import { enumSchema } from './../../utils/enum.utils';

export interface Product {
  id: string;
  created?: Date;

  packageAssignmentGuid?: string; // A guid to identify all products belonging to a single product package
  isPackageParent: boolean; // Field indicating if the current product is a parent for a product package

  // Only set when this product was created from a real, existing ProductPackage entity. Undefined if copied/converted from another project assignment instead - see sourcePackageGuid.
  sourcePackageId?: string;

  // Always set when assigned from a package, real or converted - unlike sourcePackageId, which is only set for real packages.
  // Equal to sourcePackageId when copied from a real ProductPackage, or the id of the converted product acting as the source package when there's no real ProductPackage to reference.
  sourcePackageGuid?: string;

  // Denormalized name/code of the source package, always set alongside sourcePackageGuid (denormalized rather than derived, since a converted-product source has no ProductPackage row to derive them from).
  sourcePackageName?: string;
  sourcePackageCode?: string;

  // Id of the source item this product was copied from: a ProductPackageItem.id when copied from a real package, or the source Product.id when copied from a converted product. No FK - purely informational.
  sourcePackageItemId?: string;

  packageItemQuantity?: number; // Quantity multiplier for the current product in a package

  productTemplateId?: string;
  standalone: boolean;

  phaseId: string;
  subPhaseId?: string;
  activityId: string;

  name: string;
  referenceCode: string;
  type: string;
  unitOfMeasure: string;
  notes: string;

  order: number;

  skillsRequired: string;
  specialTools: string;
  supplier: string;
  supplierCode: string;
  unionCode?: string;
  unionName?: string;

  labourCostMethod: LabourCostMethod;
  baseSalaryPerUnit: Money; // base salary per per unit. Only valid when labourCostMethod = unionPrice
  baseUnitTime: number; // base hours required to install an unit. Only valid when labourCostMethod = installationTime

  unitCost: Money;
  additionalCostsPercentage: number; // 0 to 100
  additionalSubcontractorCosts: Money;
  specialTreatmentCosts: Money;
  additionalLabourCostsPercentage: number; // 0 to 100
  quantityDocumentation: number;

  quantityPerConstructionAreas: QuantityPerConstructionArea[];
}

export interface QuantityPerConstructionArea {
  id: string;
  constructionAreaId: string;
  quantity: number;
}

export const materialAssignmentProductChatbotFields = {
  id: { type: 'string', description: 'Existing material/product id for update or delete.' },
  productTemplateId: {
    type: 'string',
    description:
      'Optional. ID of the product template which was used as basis for this product. While updating should be only set if the intend is to change it.',
  },
  phaseId: {
    type: 'string',
    description: 'Required when adding a material. Othwerise should be only set if the intend is to change it.',
  },
  code: {
    type: 'string',
    description: 'Required when adding a material. Otherwise should be only set if the intend is to change it.',
  },
  name: {
    type: 'string',
    description: 'Required when adding a material. Otherwise should be only set if the intend is to change it.',
  },
  packageAssignmentGuid: {
    type: 'string',
    description:
      'Optional. Shared by the package parent and all its package materials. Set it on an added material to put it into an existing package (use the parent packageAssignmentGuid), or generate a new guid when creating a new package. Otherwise should be only set if the intend is to change it.',
  },
  isPackageParent: {
    type: 'boolean',
    description:
      'Optional. True only for the package parent row. Package materials (children) must have false. Should be only set when adding a material.',
  },
  packageItemQuantity: {
    type: 'number',
    description:
      'Optional. Only for package materials (children). Quantity multiplier of the material per one unit of the package parent. Otherwise should be only set if the intend is to change it.',
  },
  unitOfMeasure: { type: 'string', description: 'Optional.' },
  quantityDocumentation: {
    type: 'number',
    description: 'Optional. Describes the amount of products which was specified in some project specification.',
  },
  unitCost: getMoneySchema('Describes the cost of purchasing this item.'),
  additionalCostsPercentage: { type: 'number', description: 'Optional.' },
  additionalSubcontractorCosts: getMoneySchema('Optional.'),
  specialTreatmentCosts: getMoneySchema('Optional.'),
  additionalLabourCostsPercentage: { type: 'number', description: 'Optional.' },
  baseUnitTime: {
    type: 'number',
    description:
      'Must be set when the LabourCostMethod is InstallationTime. When updating should be only specified if the intend is to change it.',
  },
  baseSalaryPerUnit: getMoneySchema(
    'Must be set when the LabourCostMethod is UnionPrice. Otherwise should be only set if the intend is to change it.'
  ),
  labourCostMethod: enumSchema('LabourCostMethod', LabourCostMethod),
  quantityPerConstructionAreas: {
    type: 'array',
    description:
      'Quantities for currently visible/available construction areas. If this field is set then it must contain a full list of quantities. The system will take all the items and replace it.',
    items: {
      type: 'object',
      properties: {
        id: { type: 'string', description: 'Existing quantity row id when updating.' },
        constructionAreaId: { type: 'string', description: 'Construction area id.' },
        quantity: { type: 'number', description: 'Quantity assigned to the construction area.' },
      },
    },
  },
};
